import React from "react";
import {
  Box,
  makeStyles,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import PropTypes from "prop-types";
import { DateTime } from "luxon";
import { strings, utils } from "common";
import { joiDateFormat } from "../../constants";
import ModalButtons from "../Common/ModalButtons";
import { usePhysicalCasesDepart } from "../../utils/cases";
import { CaseDataSchema } from "../../interfaces/case.interface";

const useStyles = makeStyles((theme) => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  modalHeader: {
    width: "100%",
    paddingBottom: theme.spacing(7),
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  modalContent: {
    width: "600px",
    background: "white",
    outline: "none",
    padding: theme.spacing(12, 12, 12, 12),
    maxHeight: "90%",
    overflow: "hidden",
    overflowY: "scroll",
  },
  tableContent: {
    maxHeight: "300px",
    overflow: "hidden",
    overflowY: "scroll",
  },
  input: {
    marginTop: theme.spacing(8),
    marginBottom: theme.spacing(4),
  },
  closeIcon: {
    cursor: "pointer",
  },
}));

interface MarkDepartedModalProps {
  open: boolean;
  setOpen: (val: boolean) => void;
  selectedrows: Array<CaseDataSchema>;
  deSelectAll: () => void;
  setErrorModal?: (val: string | null) => void;
}

const MarkDepartedModal = ({ open, setOpen, selectedrows, deSelectAll, setErrorModal }: MarkDepartedModalProps) => {
  const classes = useStyles();
  // Departure date is optional and defaults to today.
  const [departedDate, setDepartedDate] = React.useState<string>(DateTime.now().toFormat(joiDateFormat));
  const { mutateAsync: departPhysicalCases, isLoading } = usePhysicalCasesDepart();

  const handleSubmit = async () => {
    try {
      await departPhysicalCases({
        ids: selectedrows.map((row) => row.id),
        departedDate: departedDate || null,
      });
      setOpen(false);
      deSelectAll();
    } catch (error) {
      if (setErrorModal) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        setErrorModal(error.description);
      }
    }
  };

  return (
    <Modal id="markDepartedModal" className={classes.modal} open={open}>
      <Box className={classes.modalContent}>
        <Box className={classes.modalHeader}>
          <Typography variant="h3">{strings.mark_as_departed_title}</Typography>
          <CloseIcon id="closeMarkDepartedModal" className={classes.closeIcon} onClick={() => setOpen(false)} />
        </Box>

        <Typography variant="body2" gutterBottom>
          {`${selectedrows.length} ${strings.cases_selected}`}
        </Typography>

        <Box className={classes.tableContent}>
          <Table stickyHeader aria-label="table" size="small">
            <TableHead>
              <TableRow>
                <TableCell>{strings.pcid}</TableCell>
                <TableCell>{strings.wine}</TableCell>
                <TableCell>{strings.vintage}</TableCell>
                <TableCell>{strings.rotation}</TableCell>
                <TableCell>{strings.status}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedrows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.id}</TableCell>
                  <TableCell>{row.wineName}</TableCell>
                  <TableCell>{row.vintage}</TableCell>
                  <TableCell>{row.rotationNumber}</TableCell>
                  <TableCell>{row.status}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        <TextField
          type="date"
          id="departureDate"
          label={strings.departure_date}
          InputLabelProps={{
            shrink: true,
          }}
          className={classes.input}
          fullWidth
          value={departedDate}
          onChange={(e) =>
            setDepartedDate(
              utils.date.isValidDate(e.target.value)
                ? DateTime.fromJSDate(new Date(e.target.value)).toFormat(joiDateFormat)
                : ""
            )
          }
        />

        <ModalButtons
          submitId="submitMarkDepartedModal"
          cancelId="cancelMarkDepartedModal"
          actionText={strings.mark_as_departed}
          closeModal={() => setOpen(false)}
          onClick={handleSubmit}
          isSubmitting={isLoading}
        />
      </Box>
    </Modal>
  );
};

MarkDepartedModal.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  selectedrows: PropTypes.array,
  deSelectAll: PropTypes.func,
};

export default MarkDepartedModal;
